/**
 * Base API Client using native fetch
 * Shared between all resource clients
 */
import { TagadaApiError, TagadaAuthError, TagadaNetworkError, TagadaCircuitBreakerError, TagadaError, TagadaErrorCode, } from '../errors';
export class ApiClient {
    constructor(config) {
        this.currentToken = null;
        this.tokenProvider = null;
        // Circuit breaker state
        this.requestHistory = new Map();
        this.WINDOW_MS = 5000; // 5 seconds window
        this.MAX_REQUESTS = 30; // Max 30 requests per endpoint in window
        this.baseURL = config.baseURL;
        this.timeout = config.timeout || 60000; // 60 seconds for payment operations
        this.defaultHeaders = {
            'Content-Type': 'application/json',
            ...config.headers,
        };
        // Cleanup interval for circuit breaker history
        if (typeof setInterval !== 'undefined') {
            setInterval(() => this.cleanupHistory(), 10000);
        }
    }
    // Set a provider that returns a promise resolving to the token
    // This allows requests to wait until the token is ready
    setTokenProvider(provider) {
        this.tokenProvider = provider;
    }
    // Convenience methods
    async get(url, config) {
        return this.request('GET', url, undefined, config);
    }
    async post(url, data, config) {
        return this.request('POST', url, data, config);
    }
    async put(url, data, config) {
        return this.request('PUT', url, data, config);
    }
    async patch(url, data, config) {
        return this.request('PATCH', url, data, config);
    }
    async delete(url, config) {
        return this.request('DELETE', url, undefined, config);
    }
    // Update headers (useful for auth tokens)
    setHeader(key, value) {
        this.defaultHeaders[key] = value;
    }
    removeHeader(key) {
        delete this.defaultHeaders[key];
    }
    // Token management methods (matching old ApiService pattern)
    updateToken(token) {
        this.currentToken = token;
        if (token) {
            this.setHeader('x-cms-token', token);
            // console.log('[SDK] Token updated in ApiClient:', token.substring(0, 8) + '...');
        }
        else {
            this.removeHeader('x-cms-token');
            console.log('[SDK] Token removed from ApiClient');
        }
    }
    getCurrentToken() {
        return this.currentToken;
    }
    // Update configuration (useful for environment changes)
    updateConfig(config) {
        if (config.baseURL) {
            this.baseURL = config.baseURL;
        }
        if (config.timeout) {
            this.timeout = config.timeout;
        }
        if (config.headers) {
            Object.assign(this.defaultHeaders, config.headers);
        }
        console.log('[SDK] ApiClient configuration updated');
    }
    // ---- Core request method ----
    async request(method, url, data, config) {
        const requestKey = `${method}:${url}`;
        // Circuit Breaker Check
        try {
            this.checkRequestLimit(requestKey);
        }
        catch (error) {
            console.error('[SDK] 🛑 Request blocked by Circuit Breaker:', error);
            throw error;
        }
        // Token injection
        if (!config?.skipAuth && !this.currentToken && this.tokenProvider) {
            try {
                console.log('[SDK] Waiting for token...');
                const token = await this.tokenProvider();
                if (token) {
                    this.updateToken(token);
                }
            }
            catch (error) {
                console.error('[SDK] Failed to get token from provider:', error);
            }
        }
        // Build headers
        const headers = { ...this.defaultHeaders };
        if (config?.headers) {
            Object.assign(headers, config.headers);
        }
        if (!config?.skipAuth && this.currentToken) {
            headers['x-cms-token'] = this.currentToken;
        }
        // Build URL
        let fullUrl = `${this.baseURL}${url}`;
        if (config?.params) {
            const searchParams = new URLSearchParams(config.params);
            fullUrl += `?${searchParams.toString()}`;
        }
        console.log(`[SDK] Making ${method} request to: ${fullUrl}`);
        // Timeout via AbortController
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);
        // Merge signals if caller provided one
        const signal = config?.signal
            ? anySignal([config.signal, controller.signal])
            : controller.signal;
        try {
            const fetchOptions = {
                method,
                headers,
                signal,
            };
            if (data !== undefined) {
                fetchOptions.body = JSON.stringify(data);
            }
            const response = await fetch(fullUrl, fetchOptions);
            if (!response.ok) {
                const errorData = await this.safeParseJson(response);
                throw this.toTagadaError(response.status, errorData, response.statusText);
            }
            const responseData = await response.json();
            return responseData;
        }
        catch (error) {
            if (error instanceof TagadaError) {
                throw error;
            }
            // AbortError → timeout (from our controller) or caller abort
            if (error instanceof DOMException && error.name === 'AbortError') {
                throw new TagadaApiError('Request timed out', 0, {
                    code: TagadaErrorCode.TIMEOUT,
                    retryable: true,
                });
            }
            // TypeError → network failure (DNS, CORS, offline, etc.)
            if (error instanceof TypeError) {
                throw new TagadaNetworkError(error.message);
            }
            throw error instanceof Error ? error : new Error(String(error));
        }
        finally {
            clearTimeout(timeoutId);
        }
    }
    // ---- Error mapping ----
    /** Convert a non-ok fetch response into the appropriate TagadaError subclass. */
    toTagadaError(status, data, statusText) {
        const serverMessage = data?.message ??
            data?.error ??
            statusText;
        // Auth failures
        if (status === 401 || status === 403) {
            return new TagadaAuthError(serverMessage, status);
        }
        // Rate-limited
        if (status === 429) {
            return new TagadaApiError(serverMessage, 429, {
                code: TagadaErrorCode.RATE_LIMITED,
                retryable: true,
            });
        }
        // Not found
        if (status === 404) {
            return new TagadaApiError(serverMessage, 404, {
                code: TagadaErrorCode.NOT_FOUND,
                retryable: false,
            });
        }
        // Generic API error
        return new TagadaApiError(serverMessage, status, {
            code: data?.code ?? TagadaErrorCode.API_ERROR,
            details: data,
            retryable: status >= 500,
        });
    }
    // ---- Helpers ----
    async safeParseJson(response) {
        try {
            return await response.json();
        }
        catch {
            return undefined;
        }
    }
    // Circuit Breaker Implementation
    checkRequestLimit(key) {
        const now = Date.now();
        const history = this.requestHistory.get(key);
        if (!history) {
            this.requestHistory.set(key, { count: 1, firstRequestTime: now });
            return;
        }
        if (now - history.firstRequestTime > this.WINDOW_MS) {
            // Window expired, reset
            this.requestHistory.set(key, { count: 1, firstRequestTime: now });
            return;
        }
        history.count++;
        if (history.count > this.MAX_REQUESTS) {
            throw new TagadaCircuitBreakerError(`Circuit Breaker: Too many requests to ${key} (${history.count} in ${this.WINDOW_MS}ms)`);
        }
    }
    cleanupHistory() {
        const now = Date.now();
        for (const [key, history] of this.requestHistory.entries()) {
            if (now - history.firstRequestTime > this.WINDOW_MS) {
                this.requestHistory.delete(key);
            }
        }
    }
}
/**
 * Combine multiple AbortSignals into one that aborts when any input signal aborts.
 */
function anySignal(signals) {
    const controller = new AbortController();
    for (const signal of signals) {
        if (signal.aborted) {
            controller.abort(signal.reason);
            return controller.signal;
        }
        signal.addEventListener('abort', () => controller.abort(signal.reason), {
            once: true,
            signal: controller.signal,
        });
    }
    return controller.signal;
}
